import React, { useState } from "react";
import { Crown, X, Check, HardDrive } from "lucide-react";
import SubscriptionButton from "./subscriptionButton.tsx";
import { STRIPE_CONFIG } from "../../config/stripe";

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUsage: number;
  storageLimit: number;
  userData: string;
  reason: string;
  alreadyTrialed?: boolean;
}

export default function UpgradeModal({
  isOpen,
  onClose,
  currentUsage,
  storageLimit,
  userData,
  reason,
  alreadyTrialed,
}: UpgradeModalProps) {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");

  if (!isOpen) return null;

  const usagePercentage = (currentUsage / storageLimit) * 100;
  const monthlyPrice = 25;
  const annualPrice = 250;
  const savings = (monthlyPrice * 12 - annualPrice).toFixed(0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 relative overflow-hidden border border-gray-200">

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Crown className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Upgrade to DrillFlo Pro – Start Free</h2>
          </div>
          <p className="text-white/90 text-sm leading-relaxed">
            {reason === "collab"
              ? "Collaborate and share your drill designs with other users."
              : "You've reached your storage limit. Upgrade to DrillFlo Pro to unlock more storage and powerful features."}
          </p>
        </div>

        {/* Body */}
        <div className="p-6">

          {/* Usage Bar */}
          {(!reason || reason === "storage") && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-gray-500" />
                  <span>Storage Used</span>
                </div>
                <span>{usagePercentage.toFixed(0)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-blue-600"
                  style={{ width: `${usagePercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {currentUsage} of {storageLimit} drills used
              </p>
            </div>
          )}

          {/* Plan Card */}
          <div className="border rounded-xl p-5 shadow-sm bg-gray-50 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800">DrillFlo Pro</h3>
              <div className="text-right">
                <p className="text-blue-600 font-bold text-base">
                  {billingCycle === "monthly"
                    ? `$${monthlyPrice}/mo`
                    : `$${annualPrice}/year`}
                </p>
                {billingCycle === "annual" && (
                  <p className="text-xs text-green-600 font-medium">
                    Save ${savings}/year
                  </p>
                )}
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Unlimited Storage
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Unlimited Performers
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Real-time Collaboration
              </li>
            </ul>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center gap-4 mb-4">
            {["monthly", "annual"].map((cycle) => (
              <button
                key={cycle}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${billingCycle === cycle
                  ? "bg-blue-500 text-white border-blue-500"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                onClick={() => setBillingCycle(cycle as "monthly" | "annual")}
              >
                {cycle === "monthly" ? "Monthly" : "Annual"}
              </button>
            ))}
          </div>

          {/* Free Trial Note */}
          <div className="text-center mb-4">
            {alreadyTrialed ? (
              <span className="inline-block bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                You've already trialed DrillFlo Pro.
              </span>
            ) : (
              <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                🎉 {process.env.REACT_APP_DRILLFLO_PRO_TRIAL_LENGTH || 15}-Day Free Trial — Cancel anytime
              </span>
            )}
          </div>

          {/* Subscribe CTA */}
          <SubscriptionButton
            priceId={STRIPE_CONFIG.prices.professional[billingCycle]}
            userId={userData}
            trialLength={process.env.REACT_APP_DRILLFLO_PRO_TRIAL_LENGTH || 15}
            planName="Pro"
            alreadyTrialed={alreadyTrialed}
          />

          {/* Trust Note */}
          <p className="text-center text-xs text-gray-500 mt-3">
            Credit card required. You won’t be charged until your trial ends.
          </p>

          {/* Help */}
          <p className="text-center text-sm text-gray-400 mt-4">
            Questions? <a href="/support" className="text-blue-500 hover:underline">Contact Support</a>
          </p>
        </div>
      </div>
    </div>

  );
}
